// The words for every admin refusal, and the error the admin api throws (src/admin/api.js). Pure: it
// imports nothing, so tests/e2e/admin.spec.js checks the words on their own.
//
// A refusal is an AdminError: { code, status, message, body }. message is always the line to show:
// a function's own message (10.4.1 gives every one), a database refusal's own sentence, or 1 of WORDS.

// The lines for refusals that come without words of their own. 10.4.1's common refusals use the same
// words (_shared/member.ts and _shared/staff.ts), so a refusal reads the same whichever way the write went.
export const WORDS = {
  fallback: 'Something went wrong. Try again.',
  offline: "That didn't load. Check your connection and try again.",
  notAllowed: 'Only an owner or admin can do that.',
  ownerOnly: 'Only an owner can do that.',
  viewingAs: "You're viewing as a member, so nothing here can be changed.",
  twoStep: 'Staff logins need 2-step login for this. Open Admin and enter your 2-step code, then try again.',
  loginRanOut: 'Your login has run out. Log in again.',
  notThere: "That isn't there any more, or it isn't yours to change.",
  already: "That's already there.",
  missing: 'Something is missing. Fill it in and try again.',
  notSaved: "That can't be saved like that. Check it and try again.",
}

export class AdminError extends Error {
  // code: the function's error code, the database's SQLSTATE (42501, 23514, ...), or one of
  // 'failed' (no answer), 'not_there' (nothing changed), 'bad_args'; status: the HTTP status, 0 when
  // there was none; body: a function's whole answer, for extra fields (person_id, rows, space_id)
  constructor(code, status, message, body = null) {
    super(message || WORDS.fallback)
    this.name = 'AdminError'
    this.code = code
    this.status = status
    this.body = body
  }
}

// The database's own refusals start with a capital letter and are written as sentences in the hub's
// migrations ('A register is taken only for a session that has started'); Postgres's and PostgREST's
// own messages ('permission denied for table ...', 'new row violates ...') don't, and never reach a screen.
const sentence = m => (typeof m === 'string' && /^[A-Z]/.test(m) && !/JWT|PGRST|relation|column|syntax|permission denied/i.test(m)
  ? (/[.?]$/.test(m) ? m : `${m}.`) : null)

// A lib/functions.js HubFunctionError: a function's refusal, with its own words
const isFunctionError = e => e && e.name !== 'AdminError' && typeof e.code === 'string' && typeof e.status === 'number' && 'body' in e

// Any thrown thing as an AdminError with the words to show
export function adminError(e) {
  if (e instanceof AdminError) return e
  if (isFunctionError(e)) {
    let message = e.message
    if (e.code === 'viewing_as') message = WORDS.viewingAs
    else if (e.code === 'two_step_needed' && !message) message = WORDS.twoStep
    else if (e.code === 'not_logged_in') message = WORDS.loginRanOut
    else if (e.code === 'failed' && !e.status) message = WORDS.offline
    return new AdminError(e.code, e.status, message || WORDS.fallback, e.body)
  }
  // a fetch that never got an answer ('Failed to fetch', 'Load failed', 'NetworkError ...')
  if (e?.name === 'AbortError' || (e instanceof TypeError && /fetch|network|load failed/i.test(e.message || ''))) {
    return new AdminError('failed', 0, WORDS.offline)
  }
  const code = e?.code || 'failed'
  // only the database's own refusals (a SQLSTATE) carry a sentence worth showing; a bug's message doesn't
  const own = /^[0-9A-Z]{5}$/.test(code) ? sentence(e?.message) : null
  if (code === 'PGRST301' || code === 'PGRST302' || /JWT expired/i.test(e?.message || '')) return new AdminError('not_logged_in', 401, WORDS.loginRanOut)
  if (code === 'PGRST116') return new AdminError('not_there', 406, WORDS.notThere)
  if (code === '42501') return new AdminError(code, 403, own || WORDS.notAllowed)
  if (code === '23505') return new AdminError(code, 409, own || WORDS.already)
  if (code === '23502') return new AdminError(code, 400, own || WORDS.missing)
  if (['23514', '23503', '22023', '22P02', '22007', 'P0001'].includes(code)) return new AdminError(code, 400, own || WORDS.notSaved)
  if (!e?.code && e?.message && /fetch|network|load failed/i.test(e.message)) return new AdminError('failed', 0, WORDS.offline)
  return new AdminError(code, 0, own || WORDS.fallback)
}

// The words for any thrown thing, for a screen that only needs the line
export const wordsFor = e => adminError(e).message
